import Stack from "@mui/material/Stack";
import {
  EUploadingFileStatus,
  IUploadingFile,
  FileTypeENUM,
} from "@/widgets/file-uploader-widget/interface";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import { CircularProgress, IconButton, Menu, MenuItem } from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { FONT_FAMILY } from "@/utils/font-family";
import { MouseEvent, useState } from "react";
import { useRecoilState } from "recoil";
import { printHouseProfile } from "@/store/print-house-profile";
import { getTempUserToken, getUserToken } from "@/services/storage-data";
import { boardMissionsDetailsState } from "@/widgets/production-floor/state/boards";
import { DotsLoader } from "@/components/dots-loader/dots-Loader";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import axios from "axios";

import { deleteJobFileApi } from "@/services/api-service/production-floor/production-floor-endpoints";
import { useGomakeAxios } from "@/hooks";
import { GoMakeDeleteModal } from "@/components";
import { ModalType } from "@/enums";

const FileComponent = ({
  fileName,
  fileStatus,
  filePath,
  orderItemId,
  productType,
  fileType,
  onFileDeleted,
  downloadFilePath,
  bucketKey,
}: IUploadingFile & {
  onFileDeleted?: (fileName: string, fileType: FileTypeENUM) => void;
}) => {
  const { successColor, errorColor, primaryColor, grayColor, warningColor } =
    useGomakeTheme();
  const { t } = useTranslation();
  const direction = t("direction");
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [companyProfile] = useRecoilState(printHouseProfile);
  const [boardMissions] = useRecoilState(boardMissionsDetailsState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { query } = useRouter();
  const { callApi } = useGomakeAxios();
  const { productType: queryProductType } = query;
  const isMenuOpen = Boolean(menuAnchorEl);

  const resolvedOrderItemId = orderItemId || boardMissions?.orderItemId;
  const resolvedProductType = productType || (queryProductType as string) || "";

  const onOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const onCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const onOpenDeleteModal = () => {
    onCloseMenu();
    setOpenDeleteModal(true);
  };

  const onClickDelete = async () => {
    // Same source as download / Files card (upload response.source)
    const deleteSource = downloadFilePath || filePath;
    try {
      setIsLoading(true);
      const result = await deleteJobFileApi(callApi, () => {}, {
        orderItemId: resolvedOrderItemId,
        filename: fileName,
        source: deleteSource,
        productType: resolvedProductType,
      });

      if (!result?.success) {
        throw new Error("Failed to delete order item file");
      }

      try {
        const response = await fetch(
          companyProfile.filesApiAddress + "/api/Files/DeleteFile",
          {
            method: "POST",
            body: JSON.stringify({
              orderItemId: resolvedOrderItemId,
              filename: fileName,
              source: deleteSource,
              productType: resolvedProductType,
            }),
            headers: {
              authorization: "Bearer " + getUserToken(),
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          console.error(
            `Files DeleteFile failed: HTTP ${response.status}`
          );
        }
      } catch (filesApiError) {
        // ERP delete already succeeded; don't block UI cleanup
        console.error("Files DeleteFile failed:", filesApiError);
      }

      if (onFileDeleted && fileType !== undefined) {
        onFileDeleted(fileName, fileType);
      }
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setIsLoading(false);
      setOpenDeleteModal(false);
    }
  };

  const handleDownloadFile = async (bucketName: string) => {
    onCloseMenu();
    try {
      setIsLoading(true);
      let userToken = getUserToken();
      if (!userToken) {
        userToken = getTempUserToken();
      }

      const response = await axios.post(
        `https://gomake-cloud-files-api-il-683372436778.me-west1.run.app/api/Files/CreateDownloadFilePreSignedUrl`,
        { bucketKey: bucketName },
        {
          headers: {
            authorization: "Bearer " + userToken,
          },
        }
      );

      if (response.data && typeof response.data === "string") {
        const fileResponse = await fetch(response.data);
        if (!fileResponse.ok) {
          throw new Error(`HTTP error! status: ${fileResponse.status}`);
        }

        const blob = await fileResponse.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(downloadUrl);
        link.remove();
      } else {
        console.error("Invalid response from CreateDownloadFilePreSignedUrl");
      }
    } catch (error) {
      console.error("Error getting download URL:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onClickDownload = async () => {
    onCloseMenu();
    try {
      setIsLoading(true);
      const response = await fetch(
        companyProfile.filesApiAddress + "/api/Files/downloadfile",
        {
          method: "POST",
          body: JSON.stringify({
            orderItemId: resolvedOrderItemId,
            filename: fileName,
            source: downloadFilePath,
          }),
          headers: {
            authorization: "Bearer " + getUserToken(),
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onClickCopyPath = async () => {
    try {
      let pathToCopy = downloadFilePath || filePath || fileName;

      if (companyProfile?.localFilePathMapping) {
        const mapping = companyProfile.localFilePathMapping.replace(
          /[/\\]+$/,
          ""
        );
        const filePathPart = pathToCopy.replace(/^[/\\]+/, "");
        const separator = mapping.includes("\\") ? "\\" : "/";
        pathToCopy = `${mapping}${separator}${filePathPart}`;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(pathToCopy);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = pathToCopy;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      onCloseMenu();
    } catch (error) {
      console.error("Failed to copy file path:", error);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        height={"80px"}
        alignItems={"center"}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={"3px"}
          style={{ color: primaryColor(400) }}
        >
          <TextSnippetIcon />
          <span
            style={{
              ...FONT_FAMILY.Inter(500, 14),
              color: grayColor(700),
              maxWidth: "150px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
            title={fileName}
          >
            {fileName}
          </span>
        </Stack>

        <Stack direction={"row"} alignItems={"center"} gap={"5px"}>
          {fileStatus === EUploadingFileStatus.UPLOADING ? (
            <CircularProgress size={"25px"} />
          ) : fileStatus === EUploadingFileStatus.DONE ||
            fileStatus === EUploadingFileStatus.Uploaded ||
            fileStatus === EUploadingFileStatus.UploadedToGoogleCloud ? (
            <>
              {fileStatus === EUploadingFileStatus.UploadedToGoogleCloud ? (
                <CloudDoneIcon
                  style={{
                    color: warningColor(500),
                    backgroundColor: warningColor(100),
                    width: "25px",
                    height: "25px",
                    padding: "2px",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <TaskAltIcon
                  style={{
                    color: successColor(500),
                    backgroundColor: successColor(100),
                    width: "25px",
                    height: "25px",
                    padding: "2px",
                    borderRadius: "50%",
                  }}
                />
              )}
              {isLoading ? (
                <DotsLoader />
              ) : (
                <IconButton
                  onClick={onOpenMenu}
                  sx={{ height: 22, width: 22, alignSelf: "center" }}
                  size="small"
                >
                  <MoreVertIcon sx={{ width: 20, height: 20 }} />
                </IconButton>
              )}
            </>
          ) : (
            <ErrorOutlineIcon
              style={{
                color: errorColor(500),
                backgroundColor: errorColor(100),
                width: "25px",
                height: "25px",
                padding: "2px",
                borderRadius: "50%",
              }}
            />
          )}
        </Stack>
      </Stack>
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen && !isLoading}
        onClose={onCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: direction === "rtl" ? "left" : "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: direction === "rtl" ? "left" : "right",
        }}
        marginThreshold={8}
        sx={{
          // Above production-floor Drawer (999999) and Uploads panel
          zIndex: 99999999999,
        }}
      >
        <MenuItem
          onClick={() => {
            if (bucketKey) {
              handleDownloadFile(bucketKey);
            } else {
              onClickDownload();
            }
          }}
        >
          {t("translations.download")}
        </MenuItem>
        <MenuItem onClick={onClickCopyPath}>
          {t("fileUploader.copyPath")}
        </MenuItem>
        <MenuItem onClick={onOpenDeleteModal}>{t("modal.delete")}</MenuItem>
      </Menu>
      <GoMakeDeleteModal
        modalType={ModalType.DELETE}
        openModal={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onClickDelete={onClickDelete}
        title={t("modal.delete")}
        subTitle={t("materials.modals.subTitleDeleteModal", {
          name: fileName,
        })}
        closeAfterDelete={false}
        confirmButtonDisabled={isLoading}
      />
    </div>
  );
};

export { FileComponent };
